declare var $: any;
//system
import { Component, Input, OnInit, Output, EventEmitter, AfterViewInit, OnDestroy, ViewChild, HostListener, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

//services
import { LogService } from 'src/app/services/common/log.service';
import { AlertService } from 'src/app/services/alert.service';
import { FacilityService } from 'src/app/services/facility.service';
import { AuthService } from 'src/app/services/auth.service';
import { CurrencyService } from 'src/app/services/currency.service';
import { StripeService } from 'src/app/services/stripe.service';
import { MemberService } from 'src/app/services/member.service';

//components
import { AuthBaseComponent } from 'src/app/components/auth-base/auth-base.component';
import { LoginComponent } from 'src/app/components/df-login/df-login.component';

//interfaces
import { CurrencyMeta, MenuPack } from 'src/app/models/meta';
import { MemberCurrency, Purchase, MemberModel, MemberSetting } from 'src/app/models/member';
import { StartRule, TermUnit } from 'src/app/models/enums';
import { PaymentMethod, Person } from 'src/app/models/common-models';
import { Agreement, Item, PurchasePreview, PurchaseRequest, TransitionRequest } from 'src/app/models/currency';
import { Location as LocationV2 } from 'src/app/models/location';

//utils
import { Utils } from 'src/app/services/common/utils';
import {
  calendarDateToApiStartAt,
  isFacilityCalendarToday,
  membershipTransitionError,
  resolveMembershipStartCalendarDate,
} from '../membership-transition.helpers';

//services
import { EnvironmentService } from 'src/app/services/environment.service';

//components
import { AddPaymentMethodComponent } from 'src/app/components/stripe/add-payment-method/add-payment-method.component';
import { FacilityConfig } from 'src/environments/environment.interface';
import { MemberDocument } from 'src/app/models/document';
import { PdfPreviewComponent } from '../../pdf-preview/pdf-preview.component';
import { firstValueFrom } from 'rxjs';
import { PaymentIntent } from '@stripe/stripe-js';
import { JsonPipe } from '@angular/common';

@Component({
  selector: 'df-ff-checkout',
  templateUrl: './ff-checkout.component.html',
  styleUrls: ['./ff-checkout.component.scss']
})
export class FFCheckoutComponent extends AuthBaseComponent implements OnInit, OnDestroy, AfterViewInit {
  // isAgreeToTerms is already declared elsewhere, remove duplicate
  @ViewChild('login') loginPopup!: LoginComponent;
  progressStep: number = 0;
  isLoading: boolean = true;
  progressText: string = '';
  private readonly _unsubscribe: Subject<any> = new Subject();
  @Input() public currencyMeta! : CurrencyMeta;
  @Input() public purchase! : Purchase;
  @Output() passEntry:      EventEmitter<boolean> = new EventEmitter();
  submitted:                boolean  = false;
  initialized:              boolean  = false;
  memberCurrency!:          MemberCurrency;
  subscriptionMenuIds!:     number[];
  paymentMethods:           PaymentMethod[] = [];
  memberInfo!:              MemberModel;
  personInfo!:              Person;
  selectedPaymentMethodType:string = "";
  selectedPaymentMethodId:  string = "";
  isPmDropdownOpen:           boolean = false;
  currentMember!:           any;
  memberId:                 number = 0;
  memberStripeCustomerId:   string = "";
  stripeCustomerIdSetting!: MemberSetting;
  purchaseRequest!:         PurchaseRequest;
  receiptEmail:             string = "";
  settings:                 FacilityConfig;
  selectedMenuPackId:       number = 0;
  numberOfLoadingSteps: number = 3;
  isAgreeToTerms: boolean = false;
  location: LocationV2;
  purchasePreviewData!: PurchasePreview;

  constructor(
    private router: Router
    , private logService: LogService
    , private alertService: AlertService
    , private facilityService: FacilityService
    , public activeModal: NgbActiveModal
    , private _memberService: MemberService
    , private _currencyService: CurrencyService
    , private authService: AuthService
    , private _stripeService: StripeService
    , private modalService: NgbModal
    , private _environmentService: EnvironmentService
    ,  private cdr: ChangeDetectorRef
  ) {
    super(router, logService, alertService, facilityService);
    this.settings = this._environmentService.getFacilityConfig();
  }

  override async ngOnInit() {
    await super.ngOnInit();
    this.paymentMethods = [];
  }

  async ngAfterViewInit() {
    if (!this.isLoggedIn()) {
      this.loginPopup.open();
      this.isLoading = false;
    } else {
      this.progressStep = 1;
      this.progressText = 'Fetching pack details...';
      this.cdr.detectChanges();
      // Step 1: Fetch member
      this.memberInfo = await this._memberService.getMember();
      
      // Step 2: Fetch customer details and payment methods
      // Fetch person, stripeCustomerIdSetting, and paymentMethods in parallel
      this.progressStep = 2;
      this.progressText = 'Fetching member details and payment methods...';
      const [personInfo, stripeCustomerIdSetting, paymentMethods] = await Promise.all([
        this.memberInfo ? this._memberService.getPerson() : Promise.resolve(null),
        this.memberInfo ? this._memberService.getMemberSetting(this.memberInfo.Id, ('stripe_customer_id' + (this._environmentService.getFacilityConfig().stripe.isSandbox ? '_test' : ''))) : Promise.resolve(null),
        this.memberInfo ? this._stripeService.getCustomerPaymentMethods(this.memberInfo.Id) : Promise.resolve([])
      ]);
      if (this.purchase && this.purchase.Items.length > 0) {
        this.selectedMenuPackId =this.purchase.MainItemId;
      }
      this.memberInfo = this.memberInfo;
      if (this.memberInfo) {
        if (personInfo) {
          this.personInfo = personInfo;
          this.receiptEmail = this.personInfo.Email;
        }
        if (stripeCustomerIdSetting) {
          this.stripeCustomerIdSetting = stripeCustomerIdSetting;
          if (this.stripeCustomerIdSetting && this.stripeCustomerIdSetting.Id > 0) {
            this.memberStripeCustomerId = this.stripeCustomerIdSetting.Value;
          }
        }
        
        this.paymentMethods = paymentMethods;
        this.computeDefaultSelected();
        
        // Step 3: Fetch member documents if any agreements are required
        this.progressStep = 3;
        this.progressText = 'Preparing and fetching member documents...';

        await this.purchasePreview();
      }
      this.initialized = true;
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  getFormattedPrice(price: number)
  {
    price = price / 100;
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return formatter.format(price);
  }

  getTermDuration(item: Item) {
    if(item.IsSubscription)
      return "per " + Utils.getEnumNameFromValue(TermUnit, item.TermUnit).toLowerCase();
    return "";
  }

  hasDiscountLabel(item: Item): boolean {
    var discount = 
      (item.AmountInfo.FullCycleBreakup.DiscountAmount ?? 0) +
      (item.AmountInfo.FullCycleBreakup.AdditionalDiscountAmount ?? 0);
    if(discount && discount > 0)
    {
      return true;
    }
    return false;
  }

  getDiscountLabel(item: Item): string {
    var discount = 
      (item.AmountInfo.FullCycleBreakup.DiscountAmount ?? 0) +
      (item.AmountInfo.FullCycleBreakup.AdditionalDiscountAmount ?? 0);
    if(discount && discount > 0)
    {
      return "Save $" + (discount / 100);
    }
    return "";
  }

  // removeItem(itemId: number)
  // {
  //   this.purchase.Items = this.purchase.Items.filter(i => i.Id !== itemId);
  //   if(this.purchase.Items.length === 0 || this.purchase.Total === 0)
  //     this.closeModal(true);
  // }
  
  getTotalItems()
  {
    return this.purchase.Items.length + (this.purchase.Items.length > 1 ? " Items" : " Item");
  }

  private isTruthyFlag(value: any): boolean {
    if (value === true || value === false) { return value; }
    if (value === 1 || value === 0) { return !!value; }
    if (typeof value === 'string') {
      const v = value.trim().toLowerCase();
      return v === 'true' || v === '1' || v === 'yes';
    }
    return false;
  }

  private computeDefaultSelected(): void {
    // 1) Try top-level default flags
    let defaultPm = (this.paymentMethods as any[]).find(pm => this.isTruthyFlag(pm?.is_default) || this.isTruthyFlag(pm?.default));

    // 2) Prefer nested default flags if top-level not present
    if (!defaultPm) {
      defaultPm = (this.paymentMethods as any[]).find(pm =>
        ((pm?.type === 'card' || (!!pm.card && !(pm.type||'').toString())) && this.isTruthyFlag(pm?.card?.is_default)) ||
        ((pm?.type === 'us_bank_account' || pm?.type === 'bank_account' || (!!pm.us_bank_account && !(pm.type||'').toString())) && this.isTruthyFlag(pm?.us_bank_account?.is_default))
      );
    }
    if (defaultPm) {
      this.selectedPaymentMethodId = this.getPaymentMethodId(defaultPm);
      this.selectedPaymentMethodType = defaultPm.type;
      return;
    }

    // Fallback to first payment method
    if (!this.selectedPaymentMethodId && this.paymentMethods.length > 0) {
      this.selectedPaymentMethodId = this.getPaymentMethodId((this.paymentMethods as any[])[0]);
      this.selectedPaymentMethodType = ((this.paymentMethods as any[])[0]).type;
    }
  }

  isProrated(item: Item): boolean {
    return item.StartRule == StartRule.ProratedFirstCycle && item.AmountInfo.ChargableBreakup.SubTotal != item.AmountInfo.FullCycleBreakup.SubTotal;
  }

  private facilityTimeZone(): string {
    return Utils.resolveSessionTimeZone(this.settings?.timezone);
  }

  private resolveStartCalendarDate(): string {
    return resolveMembershipStartCalendarDate(this.purchase?.StartAt, this.facilityTimeZone());
  }

  private resolveStartAt(): Date {
    return calendarDateToApiStartAt(this.resolveStartCalendarDate());
  }

  private isStartAtToday(calendarDate: string): boolean {
    return isFacilityCalendarToday(calendarDate, this.facilityTimeZone());
  }

  private isMembershipTransition(): boolean {
    return !!(this.purchase?.BookingId && this.purchase.BookingId > 0);
  }

  async purchasePreview(): Promise<void> {
    this.submitted = true;
    this.isLoading = true;
    try {
      const calendarDate = this.resolveStartCalendarDate();
      const startAt = calendarDateToApiStartAt(calendarDate);
      this.purchaseRequest = {
        MemberId: this.memberInfo.Id,
        MenuPackId: this.purchase.Items[0].Id,
        StripeCustomerId: this.memberStripeCustomerId,
        StripeCardId: this.selectedPaymentMethodId,
        ReceiptEmail: this.receiptEmail,
        StartAt: startAt
      };
      // DFS-2020: Upgrade Now preview only — prorated leftover when StartAt is the facility's today.
      if (this.isMembershipTransition() && this.isStartAtToday(calendarDate)) {
        this.purchaseRequest.FromBookingId = this.purchase.BookingId;
      }
      const result = await firstValueFrom(this._currencyService.purchasePreview(this.purchaseRequest));
      if (result && result.Success) {
        this.purchasePreviewData = result.Data;
      } else {
        this.alertService.error(result?.Error || 'An error occurred', true, true);
        this.submitted = false;
        this.isLoading = false;
        this.closeModal(false);
        window.location.href = '/purchase';
        return;
      }
    } catch (error) {
      this.logService.error('purchasePreview error', error);
      this.alertService.error('An unexpected error occurred. Please try again.', true, true);
      this.closeModal(false);
      window.location.href = '/purchase';
      return;
    } finally {
      this.submitted = false;
      this.isLoading = false;
      
    }
  }

  checkout() {
    this.submitted = true;
    const startAt = this.resolveStartAt();
    const menuPackId = this.purchase.Items[0].Id;

    if (this.isMembershipTransition()) {
      const transitionRequest: TransitionRequest = {
        MemberId: this.memberInfo.Id,
        BookingId: this.purchase.BookingId!,
        ToMenuPackId: menuPackId,
        StripeCustomerId: this.memberStripeCustomerId,
        StripeCardId: this.selectedPaymentMethodId,
        ReceiptEmail: this.receiptEmail,
        StartAt: startAt
      };

      this._currencyService.transition(transitionRequest).subscribe((result: boolean | string) => {
        const failure = membershipTransitionError(result);
        if (failure) {
          this.submitted = false;
          this.alertService.error(failure);
          this.closeModal(false);
          return;
        }
        this.submitted = false;
        this.alertService.success('Membership updated successfully.', true, true);
        this.closeModal(true);
        window.location.href = '/purchase';
      });
      return;
    }

    this.purchaseRequest = {
      MemberId: this.memberInfo.Id,
      MenuPackId: menuPackId,
      StripeCustomerId: this.memberStripeCustomerId,
      StripeCardId: this.selectedPaymentMethodId,
      ReceiptEmail: this.receiptEmail,
      StartAt: startAt
    };

    this._currencyService.purchase(this.purchaseRequest).subscribe((result: boolean | string) => {
      if(typeof(result) === 'string')
      {
        this.submitted = false;
        this.alertService.error(result);
        // window.alert(result);
        this.closeModal(false);
        return;
      } else {
        this.submitted = false;
        // window.alert('Purchase successful.');
        this.alertService.success('Purchase successful.', true, true);
        this.closeModal(true);
        window.location.href = '/purchase';
        return;
      }
    });
  }

  closeModal(result: boolean) {
    this.passEntry.emit(result);
    this.activeModal.close(result);
  }

  isLoggedIn(): boolean {
    this.currentMember = this.authService.getLoggedInMember();
    this.memberId = this.currentMember?.Id || 0;
    return !!this.currentMember && this.currentMember.Id > 0;
  }

  loggedIn(isLoggedIn: boolean) {
    if (isLoggedIn)
    {
      if(this.isLoggedIn())
      {
        
      }
    }
  }

  openAddPaymentMethodPopup() {
    setTimeout(() => {
      //open class map popup
      const modalRef = this.modalService.open(AddPaymentMethodComponent, {
        ariaLabelledBy: 'modal-basic-title',
        size: 'lg',
        centered: true,
        fullscreen: window.innerWidth <= 768,
      });

      //Passing itemIds to popup
      modalRef.componentInstance.memberId = this.memberId;
  
      //Retrieving boolean from popup
      modalRef.result.then(async (result: boolean | PaymentIntent | null) => {
        this.logService.debug("card added?:", result);
        if(result == null)
        {
          return;
        }
        if(typeof(result) === 'boolean')
        {
          if(result)
          {
            this.paymentMethods = await this._stripeService.getCustomerPaymentMethods(this.memberInfo.Id);
            this.computeDefaultSelected();
            this.progressStep = 3;
            this.progressText = 'Computing amount again...';
            await this.purchasePreview();
          }
          return;
        }
        if(typeof(result) === 'object' && (result as PaymentIntent).id)
        {
          this.selectedPaymentMethodId = (result as PaymentIntent).payment_method as string || "";
          // this.selectedPaymentMethodType = (result as PaymentIntent).payment_method_type as string || "";
          this.paymentMethods = await this._stripeService.getCustomerPaymentMethods(this.memberInfo.Id);
          this.computeDefaultSelected();
        }
      });
    }, 500);
  }

  ngOnDestroy(): void {
    this._unsubscribe.next(null);
    this._unsubscribe.complete();
  }

  cardChangeEvent(event: Event) {
    const target = event.target as HTMLSelectElement;
    if (target.value === "new") {
      this.openAddPaymentMethodPopup();
    } else {
      this.selectedPaymentMethodId = target.value;
      const pm = this.paymentMethods.find(pm => this.getPaymentMethodId(pm) === this.selectedPaymentMethodId);
      if(pm)
      {
        this.selectedPaymentMethodType = pm.type;
      } else {
        this.selectedPaymentMethodType = "";
      }
    }
  }

  // Helpers for custom payment method dropdown
  isDefaultPaymentMethod(pm: any): boolean {
    if (!pm) { return false; }
    const type = (pm.type || '').toString();
    if (type === 'card' || (!!pm.card && !type)) { return !!pm.card?.is_default; }
    if (type === 'us_bank_account' || type === 'bank_account' || (!!pm.us_bank_account && !type)) { return !!pm.us_bank_account?.is_default; }
    return false;
  }

  getPaymentMethodId(pm: any): string {
    if (!pm) { return ''; }
    const type = (pm.type || '').toString();
    if (type === 'card' || (!!pm.card && !type)) { return pm.card?.id || pm.id || ''; }
    if (type === 'us_bank_account' || type === 'bank_account' || (!!pm.us_bank_account && !type)) { return pm.us_bank_account?.id || pm.id || ''; }
    // Fallback to top-level id if present
    return pm.id || '';
  }

  getPaymentMethodLabel(pm: any): string {
    if (!pm) { return ''; }
    const type = (pm.type || '').toString();
    if (type === 'card' || (!!pm.card && !type)) {
      const brand = (pm.card?.brand || 'Card').toString();
      const last4 = pm.card?.last4 || '';
      return `${this.toTitleCase(brand)} •••• ${last4}`;
    }
    if (type === 'us_bank_account' || type === 'bank_account' || (!!pm.us_bank_account && !type)) {
      const bank = pm.us_bank_account?.bank_name || 'Bank Account';
      const last4 = pm.us_bank_account?.last4 || '';
      return `${bank} •••• ${last4}`;
    }
    return 'Payment Method';
  }

  getPaymentMethodIcon(pm: any): string {
    const base = 'assets/images/svg/cards/';
    if (!pm) { return base + 'card.svg'; }
    const type = (pm.type || '').toString();
    if (type === 'card' || (!!pm.card && !type)) {
      const brand = (pm.card?.brand || '').toLowerCase();
      if (brand.includes('visa')) return base + 'visa.svg';
      if (brand.includes('master')) return base + 'mastercard.svg';
      if (brand.includes('american') || brand.includes('amex')) return base + 'american_express.svg';
      if (brand.includes('discover')) return base + 'discover.svg';
      return base + 'card.svg';
    }
    // No specific bank logos available; use generic card icon as fallback
    return base + 'card.svg';
  }

  async selectPaymentMethod(pm: any, event?: Event): Promise<void> {
    if (event) { event.stopPropagation(); }
    const id = this.getPaymentMethodId(pm);
    if (id) {
      this.selectedPaymentMethodId = id;
      this.selectedPaymentMethodType = pm.type;
      this.isPmDropdownOpen = false;
      this.progressStep = 3;
      this.progressText = 'Computing amount again...';
      await this.purchasePreview();
    }
  }

  getSelectedPaymentMethod(): any {
    return (this.paymentMethods as any[]).find(pm => this.getPaymentMethodId(pm) === this.selectedPaymentMethodId);
  }

  togglePmDropdown(event?: Event): void {
    if (event) { event.stopPropagation(); }
    this.isPmDropdownOpen = !this.isPmDropdownOpen;
  }

  setDefaultBadge(pm: any): string {
    return this.isDefaultPaymentMethod(pm) ? ' (Default)' : '';
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.isPmDropdownOpen) {
      this.isPmDropdownOpen = false;
    }
  }

  private toTitleCase(text: string): string {
    return text.replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.substr(1).toLowerCase());
  }

  getTermDurationShort(item: MenuPack) {
    var unit = Utils.getEnumNameFromValue(TermUnit, item.TermUnit).toLowerCase();
    switch(unit){
      case "month":
        return "/mo.";
      case "year":
        return "/yr.";
      default:
        return "/mo.";
    }
  }
  
  formatCurrency(amount: number){
    return Utils.formatCurrency(amount);
  }

  getDueTodayItems(): Item[] {
    return this.purchasePreviewData?.Items?.filter(i => i.AmountInfo.IsChargableNow) || [];
  }

  getUpcomingPaymentItemsCount(): number{
    return this.getUpcomingPaymentItems().length;
  }

  getUpcomingPaymentItems(): Item[] {
    return this.purchasePreviewData?.Items?.filter(i => !i.AmountInfo.IsChargableNow) || [];
  }

  getDueOnDate(item: Item): string {
    return this.purchasePreviewData?.Items?.find(i => i.Id === item.Id)?.PurchaseDate || '';
  }
  getDueOnDateStr(item: Item): string {
    const dateStr = this.purchasePreviewData?.Items?.find(i => i.Id === item.Id)?.PurchaseDateStr || '';
    return dateStr ? dateStr.charAt(0).toLowerCase() + dateStr.slice(1) : '';
  }

  getNextDueOnDateStr(item: Item): string {
    return this.purchasePreviewData?.Items?.find(i => i.Id === item.Id)?.NextChargableDateStr || '';
  }

  onAgreementAcceptanceChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.isAgreeToTerms = target.checked;
  }

  openDocumentPreview(memberDocument: Agreement) {
    const modalRef = this.modalService.open(PdfPreviewComponent, {
      size: 'lg',
      centered: true,
      fullscreen: window.innerWidth <= 768,
      windowClass: 'pdf-preview-modal'
    });
    modalRef.componentInstance.pdfUrl = memberDocument.AgreementUrl;
    modalRef.componentInstance.pdfName = memberDocument.Name;
  }
}

